import React from 'react';
import {
  type UpdatePasswordPageProps,
  useNavigation,
  useTranslation,
} from '@refinedev/core';
import {
  containerStyles,
  headStyles,
  bodyStyles,
  cardTitle,
  resetPasswordTitle,
} from './styles';
import {
  Row,
  Col,
  Layout,
  Card,
  Form,
  Input,
  Button,
  type LayoutProps,
  type CardProps,
  type FormProps,
  theme,
  message,
} from 'antd';
import { LanguageSwitcher } from '@/components/layout/changeLang';
import { AppIcon } from '../../../assets/icons';
import { motion } from 'framer-motion';
import { endPoints } from '@/api/configs';
import useAPI from '@/api/useApi';
import { useProfileStore } from '@/core';

type UpdatePasswordProps = UpdatePasswordPageProps<
  LayoutProps,
  CardProps,
  FormProps
>;

/**
 * **refine** has update password page form which is served on `/update-password` route when the `authProvider` configuration is provided.
 *
 * @see {@link https://refine.dev/docs/ui-frameworks/antd/components/antd-auth-page/#update-password} for more details.
 */
export const UpdatePasswordPage: React.FC<UpdatePasswordProps> = ({
  wrapperProps,
  contentProps,
  formProps,
}) => {
  const { push } = useNavigation();
  const { logout } = useProfileStore();
  const { token } = theme.useToken();
  const [form] = Form.useForm<{ password: string; confirm_password: string }>();
  const { translate } = useTranslation();
  const { usePost } = useAPI();
  const { isLoading, mutateAsync: updatePassword } = usePost<
    { data: string },
    { password: string; confirm_password: string }
  >(endPoints.resetPassword);

  const onSubmit = (values: { password: string; confirm_password: string }) => {
    updatePassword(
      {
        password: values.password,
        confirm_password: values.confirm_password,
      },
      {
        onSuccess: () => {
          logout();
          push('/login');
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };

  const CardTitle = (
    <>
      <div style={cardTitle}>
        <AppIcon />
        <div style={{ ...resetPasswordTitle }}>
          {translate('pages.updatePassword.title')}
        </div>
      </div>
    </>
  );

  const CardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        title={CardTitle}
        styles={{ header: headStyles, body: bodyStyles }}
        style={{
          ...containerStyles,
          backgroundColor: token.colorBgElevated,
        }}
        {...(contentProps ?? {})}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onSubmit}
            requiredMark={false}
            {...formProps}
          >
            <motion.div
              initial={{ x: -20 }}
              animate={{ x: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <Form.Item
                name="password"
                label={translate('pages.updatePassword.fields.password')}
                rules={[
                  {
                    required: true,
                    message: translate(
                      'pages.updatePassword.errors.requiredPassword'
                    ),
                  },
                ]}
                style={{ marginBottom: '12px' }}
              >
                <Input type="password" placeholder="●●●●●●●●" size="large" />
              </Form.Item>
            </motion.div>

            <motion.div
              initial={{ x: -20 }}
              animate={{ x: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <Form.Item
                name="confirm_password"
                label={translate('pages.updatePassword.fields.confirmPassword')}
                hasFeedback
                dependencies={['password']}
                rules={[
                  {
                    required: true,
                    message: translate(
                      'pages.updatePassword.errors.requiredConfirmPassword'
                    ),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          translate(
                            'pages.updatePassword.errors.confirmPasswordNotMatch'
                          )
                        )
                      );
                    },
                  }),
                ]}
              >
                <Input type="password" placeholder="●●●●●●●●" size="large" />
              </Form.Item>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  loading={isLoading}
                  block
                >
                  {translate('pages.updatePassword.buttons.submit')}
                </Button>
              </Form.Item>
            </motion.div>
          </Form>
        </motion.div>
      </Card>
    </motion.div>
  );

  return (
    <Layout {...(wrapperProps ?? {})}>
      <div
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          zIndex: 999,
        }}
      >
        <LanguageSwitcher />
      </div>

      <Row
        justify="center"
        align="middle"
        style={{
          padding: '16px 0',
          minHeight: '100dvh',
        }}
      >
        <Col xs={22}>{CardContent}</Col>
      </Row>
    </Layout>
  );
};
